import {
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
} from 'react'

import { throttle } from 'lodash-es'

import { Keys } from '@/platform/platform'
import { valueInRange } from '@/utils/utils'

export const useRewindVideo = (
  videoElementRef: RefObject<HTMLVideoElement>,
  setCurrentTime: Dispatch<SetStateAction<number>>,
) => {
  const longPressCount = useRef(0)

  const seekBack = useCallback(
    throttle(() => {
      const newCount = longPressCount.current - 1
      longPressCount.current = newCount
      progressPushing(newCount, false)
    }, 500),
    [],
  )
  const seekForward = useCallback(
    throttle(() => {
      const newCount = longPressCount.current + 1
      longPressCount.current = newCount
      progressPushing(newCount, true)
    }, 500),
    [],
  )

  useEffect(() => {
    const keyPressHandler = (e: KeyboardEvent) => {
      if (e.keyCode === Keys.MEDIA_FAST_FORWARD) {
        seekForward()
      }
      if (e.keyCode === Keys.MEDIA_REWIND) {
        seekBack()
      }
    }
    window.addEventListener('keydown', keyPressHandler)

    return () => {
      window.removeEventListener('keydown', keyPressHandler)
    }
  }, [seekBack, seekForward])

  useEffect(() => {
    const cleanRewindData = () => {
      longPressCount.current = 0
    }
    window.addEventListener('keyup', cleanRewindData)

    return () => {
      window.removeEventListener('keyup', cleanRewindData)
    }
  }, [videoElementRef, longPressCount])

  const progressPushing = useCallback(
    (longPressCount: number, toForward: boolean) => {
      if (!videoElementRef.current) {
        return
      }

      const targetTime = longPressCount <= 10 ? 10 : 30
      const { duration = 0, currentTime } = videoElementRef.current

      const newTime = toForward
        ? currentTime + targetTime
        : currentTime - targetTime

      const correctedTime = valueInRange(newTime, 0, duration)

      setCurrentTime(correctedTime)
      videoElementRef.current.currentTime = correctedTime
    },
    [videoElementRef, setCurrentTime],
  )

  return {
    seekBack,
    seekForward,
  }
}
